import {
  Required,
  IsString,
  IsNumber,
  DataValidator,
  transformKeys,
  IsArray,
  ValidateNested,
  IsBoolean,
} from "@/utils/type-vaildator";
import analytics from "@/utils/analytics";
import api, { apiV2 } from "./api";

// 请求需要的参数
interface AssetsActorRequest {
  page: number;
  pageSize: number;
}

/** 接口响应数据里面的，音频数据对象 */
export interface AssetsActor {
  id: string;
  model_id: string;
  name: string;
  gender: string;
  age_group: string;
  is_hd: boolean;
  is_pro: boolean;
  is_exclusive_pro: boolean;
  is_free_speech: boolean;
  situation?: string;
  image_url: string;
  compressed_actor_default_video_url?: string;
  full_actor_default_video_url?: string;
  sort_order: number;
  is_favorite: boolean;
  is_my_actor: boolean;
  is_free: string;
  tags?: Array<string>;
  generate_status: number;
  generate_type: number;
  default_audio: Default_audio;
};

export interface Default_audio {
  id: string;
  type: number;
  actor_id: string;
  voice_tts_id: string;
  model_name: string;
  name: string;
  age_group: string;
  language: string;
  emotion: string;
  gender: string;
  speed: number;
  audio_url: string;
  duration: number;
  is_starred: boolean;
  is_selected: boolean;
  sort_order: number;
  use_count: number;
};

// export interface AssetsActor {
//   id: string,
//   name: string,
//   modelId: string,
//   imageUrl: string,
//   gender: string,
//   createdAt: string,
//   generateStatus: string,
//   compressedActorDefaultVideoUrl: string,
//   fullActorDefaultVideoUrl: string,
//   defaultAudio: {
//     id: string,
//     name: string,
//     isStarred: boolean,
//     audioUrl: string,
//     modelName: string,
//     speed: number,
//     gender: string,
//     ageGroup: string,
//     language: string,
//     voiceTtsId: string,
//   }
// }

/** 接口返回的响应数据 */
export interface AssetsActorResponse {
  page_size: number;
  total: number;
  total_pages: number;
  current_page: number;
  actors: AssetsActor[];
}

export class DefaultAudio {
  @Required("Audio URL cannot be empty")
  @IsString("Audio URL must be a string")
  audioUrl: string;

  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("Is starred cannot be empty")
  @IsBoolean("Is starred must be a boolean")
  isStarred: boolean;

  @Required("Model name cannot be empty")
  @IsString("Model name must be a string")
  modelName: string;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;

  @Required("Speed cannot be empty")
  @IsNumber("Speed must be a number")
  speed: number;

  @Required("Voice TTS ID cannot be empty")
  @IsString("Voice TTS ID must be a string")
  voiceTtsId: string;
}

// actors 嵌套类
export class AssetsActorList {
  @Required("ID cannot be empty")
  @IsString("ID must be a string")
  id: string;

  @Required("modelId cannot be empty")
  @IsString("modelId must be a string")
  modelId: string;

  @Required("Name cannot be empty")
  @IsString("Name must be a string")
  name: string;

  @Required("imageUrl cannot be empty")
  @IsString("imageUrl must be a string")
  imageUrl: string;

  @Required("generateStatus cannot be empty")
  @IsNumber("generateStatus status must be a number")
  generateStatus: number;

  @IsString("fullActorDefaultVideoUrl must be a string")
  fullActorDefaultVideoUrl: string;

  @IsString("createdAt must be a string")
  createdAt: string;

  @IsString("compressedActorDefaultVideoUrl must be a string")
  compressedActorDefaultVideoUrl: string;

  @IsArray("tags must be an array")
  tags?: Array<string>;

  @Required("isFavorite cannot be empty")
  @IsBoolean("isFavorite must be a boolean")
  isFavorite: boolean;

  @Required("isMyActor cannot be empty")
  @IsBoolean("isMyActor must be a boolean")
  isMyActor: boolean;

  // @Required("isPro cannot be empty")
  @IsBoolean("isPro must be a boolean")
  isPro: boolean;

  // @Required("isHD cannot be empty")
  @IsBoolean("isHD must be a boolean")
  isHD: boolean;

  @Required("generateType cannot be empty")
  @IsNumber("generateType must be a number")
  generateType: number;

  @Required("defaultAudio cannot be empty")
  @ValidateNested(DefaultAudio)
  defaultAudio: DefaultAudio;

  @IsString("figureCode must be a string")
  figureCode: string;

  @IsString("figureMessage must be a string")
  figureMessage: string;

  isUnusual?: boolean;
}

// 主数据类
export class AssetsActorData {
  @Required("Page size cannot be empty")
  @IsNumber("Page size must be a number")
  pageSize: number;

  @Required("Total cannot be empty")
  @IsNumber("Total must be a number")
  total: number;

  @Required("Total pages cannot be empty")
  @IsNumber("Total pages must be a number")
  totalPages: number;

  @Required("Current page cannot be empty")
  @IsNumber("Current page must be a number")
  currentPage: number;

  // @Required("actors cannot be empty")
  @IsArray("actors must be an array")
  actors: AssetsActorList[] | null;
}

// 转换并验证 AssetsVoice 数据
const transformAndValidateAssetsActor = (
  response: AssetsActorResponse
): AssetsActorData => {
  const transformedData = transformKeys(response);

  try {
    const validData = DataValidator.validateOrThrow(transformedData, AssetsActorData);
    if (!validData.actors) {
      return {
        ...validData,
        actors: [],
      };
    }
    if (validData.actors) {
      for (const actor of validData.actors) {
        if (actor.generateStatus === 2) {
          try {
            DataValidator.validateOrThrow(actor, AssetsActorList);
          } catch (error) {
            console.error("Assets Actor data validation failed:", error.message);
            // 数据异常特殊处理 标记
            actor.isUnusual = true;
          }
        }
      }
    }
    return transformedData;
  } catch (error) {
    analytics.trackAssetsActorFail({
      data_json: JSON.stringify(transformedData),
      error_message: error.message,
    });
    console.error("Assets Actor data validation failed:", error.message);
    throw error;
  }
};

/** 获取资产演员列表 */
export const assetsActorList = async ({ page, pageSize }: AssetsActorRequest): Promise<AssetsActorData> => {
  const response = await apiV2.post<AssetsActorResponse>('/video/user-actor-template-videos', {
    current_page: page,
    page_size: pageSize,
  });
  const data = transformAndValidateAssetsActor(response.data);
  return data;
};
